// NewScreen01.js - Fixed base portion update issue
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { useMemo } from "react";
import {
  handleGetFoodType,
  handleGetPortionType,
  handleGetSpiceLevel,
  handleGetHeatingInstruction,
} from "../../../services/shef";
import { handleGetDefaultSetting } from "../../../services/default_setting";
import { toast } from "react-toastify";

const NewScreen01 = ({
  name,
  food_type_id,
  spice_level_id,
  base_type_id,
  portion_size,
  portion_type_id,
  chef_earning_fee,
  platform_price,
  delivery_price,
  description,
  reheating_instruction,
  ingredients,
  logo,
  updateFields,
  isEdit,
}) => {
  const [grams, setGrams] = useState(base_type_id === 1 ? portion_size || "" : "");
  const [pieces, setPieces] = useState(base_type_id === 2 ? portion_size || "" : "");
  const [customPortion, setCustomPortion] = useState(base_type_id === 3 ? portion_size || "" : "");
  const [spiceLevel, setSpiceLevel] = useState([]);
  const [foodType, setFoodType] = useState([]);
  const [portionTypes, setPortionTypes] = useState([]);
  const [heatInstruction, setHeatInstruction] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [activeTab, setActiveTab] = useState(base_type_id || 1);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const dishNameRef = useRef(null);


  const fileInputRef = useRef(null);
  const descriptionRef = useRef();
  const inputRefPrice = useRef(null);
  const inputRefBaseServing = useRef(null);
  const inputRefPortionSize = useRef(null);
  
  const { authToken } = useSelector((state) => state.user);

  // Preset options for grams
  const gramPresets = [250, 500, 1000];

  // Handle tab switching for portion type
  const handleTabClick = (tabNumber) => {
    setActiveTab(tabNumber);
    updateFields({ base_type_id: tabNumber });
    setShowCustomInput(false);
    setSelectedPreset(null);
    
    // Update portion_size immediately based on the tab being switched to
    if (tabNumber === 1 && grams) {
      updateFields({ portion_size: grams });
    } else if (tabNumber === 2 && pieces) {
      updateFields({ portion_size: pieces });
    } else if (tabNumber === 3 && customPortion) {
      updateFields({ portion_size: customPortion });
    }
  };

  

  const handlePresetSelect = (value) => {
    setSelectedPreset(value);
    setShowCustomInput(false);
    
    // Update both local state and parent state immediately
    if (activeTab === 1) {
      setGrams(value);
    } else if (activeTab === 2) {
      setPieces(value);
    }
    
    // Always update portion_size regardless of tab
    updateFields({ portion_size: value });
  };

  const handleCustomClick = () => {
    setShowCustomInput(true);
    setSelectedPreset('custom');
  };

  const handleGramsChange = (value) => {
    setGrams(value);
    updateFields({ portion_size: value });
  };

  const handlePiecesChange = (value) => {
    setPieces(value);
    updateFields({ portion_size: value });
  };

  const handleCustomPortionChange = (value) => {
    setCustomPortion(value);
    updateFields({ portion_size: value });
  };

  const handleIncrement = () => {
    if (activeTab === 1) {
      const newValue = parseInt(grams || 0) + 50;
      setGrams(newValue);
      updateFields({ portion_size: newValue });
    } else if (activeTab === 2) {
      const newValue = parseInt(pieces || 0) + 1;
      setPieces(newValue);
      updateFields({ portion_size: newValue });
    }
  };

  const handleDecrement = () => {
    if (activeTab === 1) {
      const newValue = Math.max(0, parseInt(grams || 0) - 50);
      setGrams(newValue);
      updateFields({ portion_size: newValue });
    } else if (activeTab === 2) {
      const newValue = Math.max(0, parseInt(pieces || 0) - 1);
      setPieces(newValue);
      updateFields({ portion_size: newValue });
    }
  };

  const handleBaseServing = (e) => {
    const value = parseInt(e.target.value, 10);
    updateFields({ portion_type_id: isNaN(value) ? "" : value });
  };

  const handlePriceChange = (e) => {
    let value = e.target.value;

    // Allow empty string for clearing
    if (value === "") {
      updateFields({ chef_earning_fee: "" });
      return;
    }

    // Allow partial inputs during typing (e.g., "12.", "12.5")
    const numericValue = parseFloat(value);
    
    // If it's a valid number, update immediately
    if (!isNaN(numericValue)) {
      updateFields({ chef_earning_fee: numericValue });
    }
  };

  const onSubmitServing = (e) => {
    try {
      // Validation
      if (activeTab === 1) {
        if (!grams || grams <= 0) {
          toast.error("Grams must be greater than 0");
          inputRefPortionSize.current?.focus();
          return;
        }
        updateFields({ portion_size: grams });
      } 
      else if (activeTab === 2) {
        if (!pieces || pieces <= 0) {
          toast.error("Pieces must be greater than 0");
          inputRefPortionSize.current?.focus();
          return;
        }
        updateFields({ portion_size: pieces });
      } 
      else if (activeTab === 3) {
        if (!customPortion || customPortion.trim() === "") {
          toast.error("Portion description is required");
          inputRefPortionSize.current?.focus();
          return;
        }
        updateFields({ portion_size: customPortion });
      }

      if (!chef_earning_fee || chef_earning_fee < 1) {
        toast.error("Base Price is Required");
        inputRefPrice.current?.focus();
        return;
      }
      if (!portion_type_id) {
        toast.error("Base Number of Serving is Required");
        inputRefBaseServing.current?.focus();
        return;
      }

      toast.success("Base Portion Added");
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  useEffect(() => {
    dishNameRef.current?.focus();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [ft, sl, pt, hi] = await Promise.all([
          handleGetFoodType(authToken),
          handleGetSpiceLevel(authToken),
          handleGetPortionType(authToken),
          handleGetHeatingInstruction(authToken),
        ]);
        setFoodType(ft);
        setSpiceLevel(sl);
        setPortionTypes(pt);
        setHeatInstruction(hi);
      } catch (e) {
        console.error("Error loading dish details", e);
      }
    })();
  }, [authToken]);

  useEffect(() => {
    if (
      spiceLevel.length > 0 &&
      (spice_level_id === "" || spice_level_id === null || spice_level_id === undefined)
    ) {
      const defaultSpice =
        spiceLevel.find(s => s.is_default)?.id ||
        spiceLevel[1]?.id ||
        spiceLevel[0]?.id;

      updateFields({ spice_level_id: defaultSpice });
    }
  }, [spiceLevel]);

  useEffect(() => {
    if (!reheating_instruction) {
      const defaultInstruction = "List the main ingredients and preparation instructions for this dish. Be specific about key ingredients and cooking methods.";
      updateFields({ reheating_instruction: defaultInstruction });
    }
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      updateFields({ logo: file });
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBoxClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (logo && typeof logo === 'string') {
      setSelectedImage(logo);
    } else if (logo instanceof File) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(logo);
    }
  }, [logo]);

  const handleSpiceLevelChange = (e) => {
    const value = parseInt(e.target.value, 10);
    updateFields({ spice_level_id: isNaN(value) ? "" : value });
  };

  const SectionHeader = ({ title, instruction }) => (
    <div className="mb-3">
      <h3 className="text-lg font-bold text-gray-800 leading-tight">{title}</h3>
      {instruction && <p className="text-xs text-gray-500 mt-1">{instruction}</p>}
    </div>
  );

  const sortedFoodType = React.useMemo(() => {
    if (!Array.isArray(foodType)) return [];

    return [...foodType].sort((a, b) => {
      const aIsPakistani = a.name?.trim().toLowerCase() === 'pakistani';
      const bIsPakistani = b.name?.trim().toLowerCase() === 'pakistani';

      if (aIsPakistani && !bIsPakistani) return -1;
      if (!aIsPakistani && bIsPakistani) return 1;
      return 0;
    });
  }, [foodType]);

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-6">
      {/* MASTER INSTRUCTIONS */}
      <div className="bg-gradient-to-r from-primaryDark to-primary text-white p-6 rounded-xl shadow-lg">
        <h1 className="text-2xl font-extrabold mb-2 text-white">Complete Your Dish Profile</h1>
        <p className="opacity-90 text-sm mb-4">Add all essential details about your dish in this screen.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="bg-white/10 p-3 rounded-lg">
            <span className="font-bold block mb-1">Step 1: Basic Info</span>
            Name, category, spice level and description.
          </div>
          <div className="bg-white/10 p-3 rounded-lg">
            <span className="font-bold block mb-1">Step 2: Details</span>
            Ingredients, serving size and pricing.
          </div>
          <div className="bg-white/10 p-3 rounded-lg">
            <span className="font-bold block mb-1">Step 3: Visual</span>
            Upload an appetizing photo of your dish.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          {/* DISH NAME */}
          <div className="bg-white p-5 rounded-xl border shadow-sm">
            <SectionHeader 
              title="Dish Name" 
              instruction="Make it sound delicious. Mention key ingredients or a family story." 
            />
            <input
              ref={dishNameRef}
              type="text"
              className="w-full text-base p-3 border rounded-lg focus:ring-2 focus:ring-primary outline-none transition-all"
              value={name}
              onChange={(e) => updateFields({ name: e.target.value })}
              placeholder="e.g. Grandma's Famous Spicy Lasagna"
              maxLength={50}
            />
            <p className="text-right text-xs text-gray-400 mt-1">{name?.length || 0}/50</p>
          </div>

          {/* FOOD TYPE */}
          <div className="bg-white p-5 rounded-xl border shadow-sm">
            <SectionHeader 
              title="Category" 
              instruction="Select the icon that best matches your meal type." 
            />
            <div className="grid grid-cols-3 gap-3">
              {sortedFoodType.map((type) => (
                <label
                  key={type.id}
                  className={`flex flex-col items-center p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
                    food_type_id === type.id ? "border-primary bg-primary/5 shadow-inner" : "border-gray-100"
                  }`}
                >
                  <img src={type.image} alt={type.name} className="h-8 w-8 mb-1" />
                  <span className="text-xs font-bold text-center">{type.name}</span>
                  <input
                    type="radio"
                    className="hidden"
                    onChange={() => updateFields({ food_type_id: type.id })}
                    checked={food_type_id === type.id}
                    name="foodType"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* SPICE LEVEL */}
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <SectionHeader title="Spice Level" instruction="How much heat should customers expect?" />
            <select
              className="w-full py-2 px-4 border rounded-xl bg-white outline-none focus:border-primary text-lg"
              value={spice_level_id ?? ""}
              onChange={(e) =>
                updateFields({ spice_level_id: Number(e.target.value) })
              }
            >
              <option value="" disabled>Select spice level</option>
              {spiceLevel.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          {/* DESCRIPTION */}
<div className="bg-white p-5 rounded-xl border shadow-sm">
  <SectionHeader 
    title="Dish Description" 
    instruction="Describe the aromas, textures, and heritage of your meal." 
  />
  <textarea
    ref={descriptionRef}
    className="w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-primary outline-none text-gray-700 leading-relaxed text-sm"
    value={description}
    onChange={(e) => updateFields({ description: e.target.value.slice(0, 250) })}
    placeholder="e.g. This slow-cooked mutton curry is a 3-generation family recipe featuring hand-ground spices..."
  />
  <p className={`text-xs text-right font-bold mt-1 ${description?.length >= 250 ? 'text-red-500' : 'text-gray-400'}`}>
    {description?.length || 0} / 250
  </p>
  {description?.length >= 400 && (
    <p className="text-red-500 text-xs mt-1">Maximum character limit of 250 reached.</p>
  )}
</div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          {/* PHOTO UPLOAD */}
          <div className="bg-white p-5 rounded-xl border shadow-sm">
            <SectionHeader 
              title="Dish Photo" 
              instruction="Upload an appetizing photo. Use natural light and a clean background." 
            />
            
            <div 
              className="w-full border-2 border-secondary border-dashed rounded-lg overflow-hidden relative cursor-pointer"
              onClick={handleBoxClick}
              style={{ minHeight: '280px' }}
            >
              {selectedImage ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  <img
                    src={selectedImage}
                    alt="Selected"
                    className="max-w-full max-h-[400px] object-contain p-2"
                  />
                  <p className="absolute top-2 right-2 z-1 bg-white rounded-md font-semibold text-xs px-3 py-1.5 shadow-sm">
                    Change Image
                  </p>
                </div>
              ) : (
                <div className="text-center flex flex-col items-center justify-center h-full p-6">
                  <svg className="sc-jSgupP ckDfJz" height="48" width="48" viewBox="0 0 33 28" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="none">
                    <path d="M10.5 5.5C8.8125 5.5 7.5 6.875 7.5 8.5C7.5 10.1875 8.8125 11.5 10.5 11.5C12.125 11.5 13.5 10.1875 13.5 8.5C13.5 6.875 12.125 5.5 10.5 5.5ZM28.9375 0H4.9375C2.75 0 0.9375 1.8125 0.9375 4V24C0.9375 26.25 2.75 28 4.9375 28H28.9375C31.125 28 32.9375 26.25 32.9375 24V4C32.9375 1.8125 31.1875 0 28.9375 0ZM29.9375 23.625L21.375 12C21.1875 11.6875 20.875 11.5 20.5 11.5C20.0625 11.5 19.75 11.6875 19.5 12L12.875 21L10.5625 18.125C10.3125 17.875 10 17.6875 9.625 17.6875C9.25 17.6875 8.9375 17.875 8.6875 18.125L4 24L3.9375 4C3.9375 3.5 4.4375 3 4.9375 3H28.9375C29.5 3 29.9375 3.5 29.9375 4V23.625Z" fill="#383838" className="sc-iBPRYJ bDhIKt"></path>
                  </svg>
                  <h3 className="text-lg font-semibold mb-1 mt-2">Upload your photo here</h3>
                  <p className="text-gray-500 text-xs">Click to browse or drag and drop</p>
                </div>
              )}
            </div>
            
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />

            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg mt-4">
              <div className="flex items-center gap-2 mb-2">
                <svg className="text-amber-600" height="16" width="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="font-bold text-amber-800 text-xs uppercase tracking-wider">Photo Tips</span>
              </div>
              <ul className="space-y-1">
                <li className="text-xs text-amber-900 flex items-start gap-2 leading-tight">
                  <span className="text-amber-500">•</span> Use a circular, white bowl or plate
                </li>
                <li className="text-xs text-amber-900 flex items-start gap-2 leading-tight">
                  <span className="text-amber-500">•</span> Take photo from above with natural light
                </li>
                <li className="text-xs text-amber-900 flex items-start gap-2 leading-tight">
                  <span className="text-amber-500">•</span> Include entire plate/bowl in frame
                </li>
                <li className="text-xs text-amber-900 flex items-start gap-2 leading-tight">
                  <span className="text-amber-500">•</span> Avoid artificial lighting and flash
                </li>
              </ul>
            </div>
          </div>

          {/* IMPROVED PORTION & PRICING SECTION */}
          <div className="bg-white p-5 rounded-xl border shadow-sm">
            <SectionHeader title="Portion & Pricing" instruction="Define exactly what the customer receives." />

            <div className="space-y-5">
              {/* Tabs */}
              <div className="flex items-center border-b gap-1">
                <button
                  onClick={() => handleTabClick(1)}
                  className={`flex-1 pb-3 px-4 text-sm font-medium transition-all ${
                    activeTab === 1 
                      ? "border-b-2 border-primary text-primary" 
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Container
                </button>
                <button
                  onClick={() => handleTabClick(2)}
                  className={`flex-1 pb-3 px-4 text-sm font-medium transition-all ${
                    activeTab === 2 
                      ? "border-b-2 border-primary text-primary" 
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Pieces
                </button>
                <button
                  onClick={() => handleTabClick(3)}
                  className={`flex-1 pb-3 px-4 text-sm font-medium transition-all ${
                    activeTab === 3 
                      ? "border-b-2 border-primary text-primary" 
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Other
                </button>
              </div>

              {/* Container Tab */}
              {activeTab === 1 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Select weight in grams
                    </label>
                    
                    {/* Preset Options */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {gramPresets.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => handlePresetSelect(preset)}
                          className={`py-3 px-2 rounded-lg border-2 font-semibold text-sm transition-all ${
                            selectedPreset === preset
                              ? "border-primary bg-primary text-white shadow-md"
                              : "border-gray-200 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"
                          }`}
                        >
                          {preset}g
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={handleCustomClick}
                        className={`py-3 px-2 rounded-lg border-2 font-semibold text-sm transition-all ${
                          selectedPreset === 'custom'
                            ? "border-primary bg-primary text-white shadow-md"
                            : "border-gray-200 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"
                        }`}
                      >
                        Custom
                      </button>
                    </div>

                    {/* Custom Input */}
                    {showCustomInput && (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          Enter Custom Weight (grams)
                        </label>
                        <div className="flex items-center justify-between bg-white rounded-lg border border-gray-300">
                          <button 
                            type="button"
                            onClick={handleDecrement} 
                            className="w-12 h-12 hover:bg-gray-100 rounded-l-lg transition-colors flex items-center justify-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                              <path d="M5 11V13H19V11H5Z"></path>
                            </svg>
                          </button>
                          <div className="flex items-center flex-1 justify-center gap-1">
                            <input
                              type="number"
                              className="text-center border-0 bg-transparent text-base font-semibold w-20 focus:outline-none"
                              ref={inputRefPortionSize}
                              placeholder="0"
                              value={grams}
                              onChange={(e) => handleGramsChange(e.target.value)}
                            />
                            <span className="text-gray-500 text-sm">grams</span>
                          </div>
                          <button 
                            type="button"
                            onClick={handleIncrement} 
                            className="w-12 h-12 hover:bg-gray-100 rounded-r-lg transition-colors flex items-center justify-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                              <path d="M11 11V5H13V11H19V13H13V19H11V13H5V11H11Z"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pieces Tab */}
              {activeTab === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Select Number of Pieces
                    </label>
                    
                    {/* Preset Options for Pieces */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {[1, 2, 4, 6].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => handlePresetSelect(preset)}
                          className={`py-3 px-2 rounded-lg border-2 font-semibold text-sm transition-all ${
                            selectedPreset === preset
                              ? "border-primary bg-primary text-white shadow-md"
                              : "border-gray-200 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"
                          }`}
                        >
                          {preset} {preset === 1 ? 'pc' : 'pcs'}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={handleCustomClick}
                      className={`w-full py-3 px-4 rounded-lg border-2 font-semibold text-sm transition-all ${
                        selectedPreset === 'custom'
                          ? "border-primary bg-primary text-white shadow-md"
                          : "border-gray-200 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"
                      }`}
                    >
                      Custom Amount
                    </button>

                    {/* Custom Input for Pieces */}
                    {showCustomInput && (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          Enter Custom Pieces
                        </label>
                        <div className="flex items-center justify-between bg-white rounded-lg border border-gray-300">
                          <button 
                            type="button"
                            onClick={handleDecrement} 
                            className="w-12 h-12 hover:bg-gray-100 rounded-l-lg transition-colors flex items-center justify-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                              <path d="M5 11V13H19V11H5Z"></path>
                            </svg>
                          </button>
                          <div className="flex items-center flex-1 justify-center gap-1">
                            <input
                              type="number"
                              className="text-center border-0 bg-transparent text-base font-semibold w-20 focus:outline-none"
                              ref={inputRefPortionSize}
                              placeholder="0"
                              value={pieces}
                              onChange={(e) => handlePiecesChange(e.target.value)}
                            />
                            <span className="text-gray-500 text-sm">pieces</span>
                          </div>
                          <button 
                            type="button"
                            onClick={handleIncrement} 
                            className="w-12 h-12 hover:bg-gray-100 rounded-r-lg transition-colors flex items-center justify-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                              <path d="M11 11V5H13V11H19V13H13V19H11V13H5V11H11Z"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Other Tab */}
              {activeTab === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Describe Your Portion
                    </label>
                    <input
                      type="text"
                      ref={inputRefPortionSize}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                      placeholder="e.g., 500gm + 2 pieces roti + salad"
                      value={customPortion}
                      onChange={(e) => handleCustomPortionChange(e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Describe the full portion in detail for customer clarity
                    </p>
                  </div>
                </div>
              )}

              {/* Base Number of Servings */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Base Number of Servings
                </label>
                <select
                  ref={inputRefBaseServing}
                  value={portion_type_id}
                  onChange={handleBaseServing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-white"
                >
                  <option value="">Select servings</option>
                  {portionTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  How many people will this portion serve?
                </p>
              </div>

              {/* Base Price */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Base Price
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg px-4 py-3 bg-white focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent">
                  <span className="text-lg font-bold text-gray-600 mr-2">PKR</span>
                  <input
                    type="number"
                    className="flex-1 border-0 bg-transparent text-base focus:outline-none"
                    placeholder="0.00"
                    ref={inputRefPrice}
                    min={0}
                    step={0.01}
                    value={chef_earning_fee || ""}
                    onChange={handlePriceChange}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Prices ending in .49 or .99 tend to perform better
                </p>
              </div>

              {/* Tips Box */}
              <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                <div className="flex items-start gap-2 mb-2">
                  <svg className="text-blue-500 mt-0.5 flex-shrink-0" height="18" width="18" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-blue-800 mb-1">Portion Tips</h4>
                    <ul className="space-y-1">
                      <li className="text-xs text-blue-700">• Single serving portions are most popular</li>
                      <li className="text-xs text-blue-700">• Be generous with portion sizes for new dishes</li>
                      <li className="text-xs text-blue-700">• Price competitively based on ingredients</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              {/* <button
                onClick={onSubmitServing}
                type="button"
                className="w-full py-3 bg-primary hover:bg-primaryDark text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg text-sm"
              >
                Save Portion Details
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* CHECKLIST */}
      <div className="bg-gray-50 p-6 rounded-xl border-2 border-primary/20">
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
            <polyline points="9 11 12 14 22 4"></polyline>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
          Screen 1 Completion Checklist
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { label: "Appetizing dish name", done: !!name },
            { label: "Food category selected", done: !!food_type_id },
            { label: "Spice level defined", done: !!spice_level_id },
            { label: "Description added", done: description?.length > 10 },
            { label: "Serving size and price set", done: !!base_type_id && !!chef_earning_fee },
            { label: "Photo uploaded", done: !!selectedImage },
          ].map((item, i) => (
            <div key={i} className={`flex items-center gap-2.5 p-2.5 rounded-lg bg-white shadow-sm border ${item.done ? "border-green-200" : "border-gray-200 opacity-60"}`}>
              {item.done ? (
                <div className="bg-green-500 text-white rounded-full p-1">
                  <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
              ) : (
                <div className="w-4 h-4 border-2 border-gray-300 rounded-full"></div>
              )}
              <span className={`text-xs ${item.done ? "text-green-700 font-medium" : "text-gray-500"}`}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewScreen01;